import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ExternalLink, FolderOpen, Loader2, Pencil, Plus, Search, Star, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { HcResourceFormDialog } from '../components/HcResourceFormDialog';
import { AdminFaqsTab } from '../components/AdminFaqsTab';
import { AdminSupportRequestsTab } from '../components/AdminSupportRequestsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/helpconnect-resources`;

const downloadDoc = async (r) => {
  try {
    const res = await axios.get(`${API}/${r.id}/document`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = r.file_name || 'document'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { toast.error('Could not download the document'); }
};

const ResourceRow = ({ r, catName, onEdit, onToggle, onDelete }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-3 p-4" data-testid={`admin-resource-row-${r.id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{r.title}</span>
        <Badge variant={r.status === 'published' ? 'default' : 'secondary'} data-testid={`admin-resource-status-${r.id}`}>{r.status === 'published' ? 'Published' : 'Draft'}</Badge>
        {r.featured && <Badge variant="outline" className="gap-1"><Star className="h-3 w-3" />Featured</Badge>}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        {catName} · {r.resource_type === 'link' ? 'Link' : `Document${r.file_name ? `: ${r.file_name}` : ' (not uploaded)'}`} · {r.states.length ? r.states.join(', ') : 'All states'}
      </div>
      {r.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.description}</p>}
    </div>
    <div className="flex flex-wrap gap-2 shrink-0">
      {r.resource_type === 'link' && r.url && <Button size="sm" variant="ghost" asChild><a href={r.url} target="_blank" rel="noreferrer" aria-label="Open link"><ExternalLink className="h-4 w-4" /></a></Button>}
      {r.file_name && <Button size="sm" variant="ghost" onClick={() => downloadDoc(r)} aria-label="Download document" data-testid={`admin-resource-download-${r.id}`}><Download className="h-4 w-4" /></Button>}
      <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`admin-resource-edit-${r.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
      <Button size="sm" variant={r.status === 'published' ? 'outline' : 'default'} onClick={() => onToggle(r)} data-testid={`admin-resource-toggle-${r.id}`}>
        {r.status === 'published' ? 'Unpublish' : 'Publish'}
      </Button>
      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(r)} aria-label="Delete" data-testid={`admin-resource-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  </div>
);

export default function HelpConnectResourcesPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [tab, setTab] = useState('resources');

  const load = useCallback(() => {
    axios.get(API).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Failed to load resources'));
  }, []);
  useEffect(() => { if (user?.role === 'system_admin') load(); }, [load, user?.role]);

  const catName = useMemo(() => Object.fromEntries((data?.categories || []).map((c) => [c.id, c.name])), [data]);
  const list = (data?.resources || []).filter((r) => !q || `${r.title} ${r.description}`.toLowerCase().includes(q.toLowerCase()));

  const toggle = async (r) => {
    try {
      await axios.post(`${API}/${r.id}/${r.status === 'published' ? 'unpublish' : 'publish'}`);
      toast.success(r.status === 'published' ? 'Resource unpublished' : 'Resource published');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the resource'); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/${toDelete.id}`); toast.success('Resource deleted'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
    setToDelete(null);
  };

  if (user?.role !== 'system_admin') {
    return <div className="p-6 text-muted-foreground" data-testid="helpconnect-resources-forbidden">Only System Administrators can manage HELPConnect resources.</div>;
  }

  return (
    <div className="space-y-6" data-testid="helpconnect-resources-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FolderOpen className="h-6 w-6" />HELPConnect Resources</h1>
          <p className="text-muted-foreground text-sm">Manage the resources, support FAQs and help desk requests for HELPConnect.</p>
        </div>
        {tab === 'resources' && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-resource-btn"><Plus className="h-4 w-4 mr-2" />Add Resource</Button>}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="resources" data-testid="tab-resources">Resources</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="tab-faqs">Support FAQs</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-support-requests">Support Requests</TabsTrigger>
        </TabsList>
        <TabsContent value="faqs"><AdminFaqsTab /></TabsContent>
        <TabsContent value="requests"><AdminSupportRequestsTab /></TabsContent>
        <TabsContent value="resources">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Resources</CardTitle>
          <CardDescription>{data ? `${data.resources.filter((r) => r.status === 'published').length} published · ${data.resources.filter((r) => r.status !== 'published').length} draft` : ''}</CardDescription>
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search resources" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-resource-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!data ? <Loader2 className="h-5 w-5 animate-spin m-6" /> : list.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground" data-testid="admin-resources-empty">{q ? 'No resources match your search.' : 'No resources yet. Click Add Resource to create one.'}</p>
          ) : (
            <div className="divide-y border-t">
              {list.map((r) => <ResourceRow key={r.id} r={r} catName={catName[r.category]} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onToggle={toggle} onDelete={setToDelete} />)}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
      {data && <HcResourceFormDialog open={formOpen} onOpenChange={setFormOpen} resource={editing} categories={data.categories} states={data.states} onSaved={load} />}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this resource?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" will be removed from HELPConnect. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-resource-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
